import { defineStore } from 'pinia'
import type { PersistentState, ImpersonationContext } from '~/types/persistent'
import { UserRole } from '~/types/account'

export const usePersistentStore = defineStore('persistent', () => {
  const persistentState = ref<PersistentState>({
    viewAsDate: null,
    demoViewAsDate: null,
    units: null,
    pwa: null,
    sharedCartId: null,
    noTranslate: null,
    enabledFeature: [],
    hybridRoute: [],
    pwaPrompt: null,
    expirationDialogTime: null,
    highlightDemo: false,
    demoReferral: null,
    isDemo: false,
    utm: {
      utmSource: undefined,
      utmMedium: undefined,
      utmCampaign: undefined
    }
  })

  // Centralized impersonation contexs

  const impersonationContext = ref<ImpersonationContext>({
    isImpersonating: false,
    impersonatedEmail: undefined,
    impersonatedRole: undefined
  })

  // agentEmail for a context being built: the one already held, else the agentEmail cookie
  // the agent sign-in wrote. No placeholder: AgentHeader supplies its own where it shows it.
  const knownAgentEmail = () => impersonationContext.value.agentEmail
    || useCookie<string | undefined>('agentEmail', { secure: true, httpOnly: false }).value
    || undefined

  // The one place an impersonation's context is set. startImpersonation begins one through it
  // (agent.impersonate, the join's test recruit), and init-auth sets it from cookies on every
  // page load. init-auth passes agentEmail from the request's cookie header: it runs after an
  // await, where SSR has no Nuxt instance for useCookie. Otherwise the agentEmail already held
  // or the cookie's is kept. AB#64477
  const setImpersonationContext = (context: Omit<ImpersonationContext, 'agentEmail'> & { agentEmail?: string }) => {
    const agentEmail = 'agentEmail' in context ? context.agentEmail : knownAgentEmail()
    impersonationContext.value = { ...context, agentEmail }
  }

  const startImpersonation = ({ impersonatedEmail, impersonatedRole }: Pick<ImpersonationContext, 'impersonatedEmail' | 'impersonatedRole'>) => {
    setImpersonationContext({ isImpersonating: true, impersonatedEmail, impersonatedRole })
  }

  // The agent's test recruit (join/agent-recruit). Context and the isAgentImpersonated cookie
  // are set together here because that sign-in does not write the cookie, unlike
  // agent/impersonate, whose response does. Session-scoped (no expiry), like isRecruit, so
  // closing the browser ends the test recruit and a later real recruit is never read as the
  // agent's. AB#64477
  const startTestRecruitImpersonation = (impersonatedEmail: string) => {
    useCookie<boolean>('isAgentImpersonated', { secure: true, httpOnly: false }).value = true
    startImpersonation({ impersonatedEmail, impersonatedRole: UserRole.Recruit })
  }

  const clearImpersonationContext = () => {
    impersonationContext.value = {
      isImpersonating: false,
      impersonatedEmail: undefined,
      impersonatedRole: undefined,
      agentEmail: impersonationContext.value.agentEmail // Preserve agentEmail when clearing impersonation
    }
  }

  const updateValue = <T extends keyof PersistentState> (key: T, value: PersistentState[T], _expire?: Date) => {
    persistentState.value[key] = value
  }

  const getPersistentValue = <T extends keyof PersistentState> (key: T) => {
    return persistentState.value[key]
  }

  // Update persistent store values without triggering the plugin to set cookies,
  // since the server now sets them on api calls and they will be set on the next page load
  // Only used client side since ultimately $auth.$storage.getCookie accesses document.cookie
  const updateStateFromServerCookies = () => {
    persistentState.value.isDemo = !!useCookie('isDemo').value
  }

  return {
    persistentState,
    updateValue,
    getPersistentValue,
    updateStateFromServerCookies,
    impersonationContext,
    setImpersonationContext,
    startImpersonation,
    startTestRecruitImpersonation,
    clearImpersonationContext
  }
})
